// src/components/branding/BrandLogo.tsx
import React, { useState } from 'react';

import { useBranding } from '../../context/BrandingContext';
import { Branding } from '../../types/branding/BrandingTypes';
import XoeMedLogo from './XoeMedLogo';
export interface BrandLogoProps {
	/** Overall lockup height in px. Also the height the placeholder reserves. */
	height?: number;
	className?: string;
	/**
	 * Which logo variant to prefer. `auto` (default) reads the live theme; `dark` and
	 * `light` force it, for a surface whose background does not follow the theme.
	 */
	variant?: 'auto' | 'light' | 'dark';
}

const isDarkTheme = (): boolean =>
	typeof document !== 'undefined' &&
	document.documentElement.classList.contains('dark-theme');

export const resolveLogoUrl = (
	branding: Branding,
	variant: BrandLogoProps['variant'] = 'auto',
): string | null => {
	if (!branding.whitelabel) return null;

	const preferDark = variant === 'dark' || (variant === 'auto' && isDarkTheme());

	return preferDark ? branding.logo_dark_url ?? branding.logo_url : branding.logo_url;
};

const BrandLogo: React.FC<BrandLogoProps> = ({ height = 34, className, variant = 'auto' }) => {
	const { branding, isResolved } = useBranding();

	const [failedUrl, setFailedUrl] = useState<string | null>(null);

	const logoUrl = resolveLogoUrl(branding, variant);

	if (!isResolved) {
		return (
			<span
				aria-hidden="true"
				className={className}
				data-testid="brand-logo-placeholder"
				style={{ display: 'inline-block', height, lineHeight: `${height}px` }}
			/>
		);
	}

	if (logoUrl && logoUrl !== failedUrl) {
		return (
			<img
				src={logoUrl}
				alt={branding.display_name ?? 'Clinic logo'}
				className={className}
				data-testid="brand-logo"
				style={{ height, width: 'auto', maxWidth: '100%' }}
				onError={() => setFailedUrl(logoUrl)}
			/>
		);
	}

	return <XoeMedLogo height={height} className={className} />;
};

export default BrandLogo;
